import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
import { FieldValues } from 'react-hook-form';
import { Cart as CartModel } from '../data/models/cart';
import { store } from '../store/configureStore';
import { OrderStatusUpdate } from '../data/models/orders/orderItemUpdateStatus';
import { ForgotPasswordRequestModel } from '../data/models/accounts/forgotPasswordRequestModel';
import { ResetPasswordRequestModel } from '../data/models/accounts/resetPasswordRequestModel';
import { ChangePasswordRequestModel } from '../data/models/accounts/changePasswordRequestModel';
import { logout, setUser } from '../../features/account/accountSlice';
import { CreateRestaurantFormData } from '../data/models/restaurants/createRestaurantFormData';
import {
  UpdateRestaurantUserFormData,
  RestaurantUserRoleDto,
} from '../data/models/restaurantUsers/restaurantUserFormdata';
import { MealItemCustomizationRules } from '../data/models/mealItems/mealItemCustomization';
import { UserProfileFormData } from '../data/models/accounts/userProfileFormData';
import {
  AvailabilityScheduleDto,
  UpdateAvailabilityScheduleDto,
} from '../data/models/common/availability';
import { OrderStatus } from '../data/models/orders/orderStatus';
import { MenuFormModel } from '../../features/manager/menus/types';
import { KitchenFormModel } from '../data/models/kitchens/kitchenFormModel';
import { MealItemFormModel } from '../data/models/mealItems/mealItemFormModel';
import {
  MealItemCustomizationModel,
  MealItemCustomizationOptionModel,
} from '../data/models/mealItems/mealItemCustomizationModel';
import type {
  RestaurantTable,
  TableFormData,
} from '../../features/manager/tables/types';
import { TableStatus } from '../../features/manager/tables/types';
import { TaxRateForm, TaxRateFormModel } from '../data/models/taxes/taxRate';
import authService from '../services/authService';

// Payment-related interfaces
export interface CreatePaymentRequest {
  orderId: string;
  restaurantId: string;
  paymentMethod: number;
  amount: number;
  amountReceived: number;
  referenceNumber?: string;
}

export interface CreateRefundRequest {
  orderId: string;
  restaurantId: string;
  paymentId: string;
  amount: number;
  reason: string;
}

export const baseUrl =
  import.meta.env.VITE_API_URL || 'https://api.smartristo.ch/api/';

axios.defaults.baseURL = baseUrl;
axios.defaults.withCredentials = true;

// Define paths that don't require authentication
const publicPaths = [
  /^customer-restaurant/, // Customer restaurant endpoints
  /^account\/(login|register|reset-password|verify-email|forgot-password|refresh-token)/, // Public account endpoints
  /^manager\/\d+\/order$/, // Create order endpoint
];

// All other paths require authentication by default
const responseBody = (response: AxiosResponse) => response.data;

interface AxiosErrorResponse {
  status?: number;
  data?: unknown;
}

// Use authService for authentication operations

axios.interceptors.request.use((config) => {
  const url = config.url || '';

  // Check if the URL matches any public path pattern
  const isPublicPath = publicPaths.some((pattern) => pattern.test(url));

  // Add auth header for non-public paths
  if (!isPublicPath) {
    // Use authService to get the token
    const token = authService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const language = store.getState().language.selectedLanguage;
  if (language) config.headers['Accept-Language'] = language.culture;
  return config;
});

axios.interceptors.response.use(
  async (response) => response,
  async (error: AxiosError<AxiosErrorResponse>) => {
    if (error.config) {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
      };
      const { status } = error.response as AxiosResponse;
      // Use authService to check if user is authenticated
      const isAuthenticated = authService.isAuthenticated();

      // Handle 401 errors
      if (status === 401) {
        console.log('Caught 401 error');
        console.log('isAuthenticated:', isAuthenticated);
        console.log('originalRequest._retry:', originalRequest._retry);
        console.log('originalRequest.url:', originalRequest.url);
        if (
          isAuthenticated &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('account/refresh-token')
        ) {
          // Try token refresh for authenticated users
          originalRequest._retry = true;
          try {
            const newAccessToken = await refreshAccessToken();
            if (newAccessToken) {
              originalRequest.headers = {
                ...(originalRequest.headers ?? {}),
                Authorization: `Bearer ${newAccessToken}`,
              };
              return axios(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error during token refresh:', refreshError);
            store.dispatch(logout());
          }
        } else {
          // For unauthenticated users or failed refresh, redirect to login
          store.dispatch(logout());
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath && currentPath !== '/') {
            window.location.href = `/account/login?returnUrl=${encodeURIComponent(
              currentPath
            )}`;
          } else {
            window.location.href = `/account/login`;
          }
        }
      }
    }

    return Promise.reject(error.response || error);
  }
);

// Track the refresh token request to prevent multiple concurrent requests
let refreshTokenRequest: Promise<string | null> | null = null;

/**
 * Refreshes the access token using the refresh token
 * This is a reusable function that can be called from anywhere
 * It ensures only one refresh request is in progress at a time
 * @returns Promise<string | null> - The new access token or null if refresh failed
 */
/**
 * Refreshes the access token using the refresh token
 * This is a reusable function that can be called from anywhere
 * It ensures only one refresh request is in progress at a time
 * @returns Promise<string | null> - The new access token or null if refresh failed
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  // Use authService to get refresh credentials
  const credentials = authService.getRefreshCredentials();
  if (!credentials) return null;

  const { refreshToken, email } = credentials;

  // If there's already a refresh request in progress, return that promise
  if (refreshTokenRequest) {
    return refreshTokenRequest;
  }

  // Create a new refresh token request
  refreshTokenRequest = axios
    .post('account/refresh-token', { email, refreshToken })
    .then((response) => {
      // Save the user data to localStorage for persistence
      authService.saveUser(response.data);
      const userWithRoles = authService.getUserFromLocalStorage();
      if (userWithRoles) {
        store.dispatch(setUser(userWithRoles));
      }

      return response.data.accessToken;
    })
    .catch((error) => {
      if ((error as AxiosError).response?.status === 401) {
        // If the refresh token is invalid, log the user out
        store.dispatch(logout());
        authService.resetUser();
      } else {
        console.error('Refresh token error:', error);
      }
      return null;
    })
    .finally(() => {
      // Clear the refresh token request
      refreshTokenRequest = null;
    });

  return refreshTokenRequest;
};

const isDevelopment = import.meta.env.DEV;
const ARTIFICIAL_DELAY_MS = 500;

const sleep = () =>
  new Promise((resolve) => setTimeout(resolve, ARTIFICIAL_DELAY_MS));

const requests = {
  get: <T>(url: string, config?: object) =>
    axios
      .get<T>(url, config)
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),

  post: (url: string, body: object, headers: object = {}) =>
    axios
      .post(url, body, {
        headers: { ...headers, 'Content-type': 'application/json' },
      })
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),

  put: (url: string, body: object) =>
    axios
      .put(url, body)
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),

  patch: (url: string, body: object) =>
    axios
      .patch(url, body)
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),

  delete: (url: string) =>
    axios
      .delete(url)
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),

  postForm: async (url: string, data: FieldValues | FormData) =>
    axios
      .postForm(url, data instanceof FormData ? data : createFormData(data), {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),

  putForm: async (url: string, data: FieldValues | FormData) =>
    axios
      .putForm(url, data instanceof FormData ? data : createFormData(data), {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),

  getBlob: (url: string, config?: object) =>
    axios
      .get<Blob>(url, { ...config, responseType: 'blob' })
      .then(responseBody)
      .then((response) =>
        isDevelopment ? sleep().then(() => response) : response
      ),
};

const createFormData = (data: FieldValues): FormData => {
  return Object.entries(data).reduce((formData, [key, value]) => {
    if (value === null || value === undefined) {
      return formData;
    }

    if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value.toString());
    }

    return formData;
  }, new FormData());
};

const Restaurant = {
  list: (nextPageId?: string) =>
    requests.get('manager/restaurant', { params: { nextPageId } }),
  details: (id: string) => requests.get(`manager/restaurant/${id}`),
  config: (id: string) => requests.get(`public/restaurant/${id}`),
  create: (restaurant: CreateRestaurantFormData) =>
    requests.postForm('manager/restaurant', restaurant),
  update: (id: string, restaurant: CreateRestaurantFormData) =>
    requests.putForm(`manager/restaurant/${id}`, restaurant),
  delete: (id: string) => requests.delete(`manager/restaurant/${id}`),
  getOpeningHours: (restaurantId: string) =>
    requests.get<AvailabilityScheduleDto[]>(
      `manager/restaurant/${restaurantId}/opening-hours`
    ),
  updateOpeningHours: (
    restaurantId: string,
    dto: UpdateAvailabilityScheduleDto
  ) => requests.put(`manager/restaurant/${restaurantId}/opening-hours`, dto),
  updateSettings: (restaurantId: string, data: { priceIncludesTax: boolean }) =>
    requests.put(`manager/restaurant/${restaurantId}/settings`, data),
};

const Order = {
  list: (restaurantId: string, nextPageId?: string) =>
    requests.get(`manager/${restaurantId}/order`, { params: { nextPageId } }),
  details: (restaurantId: string, orderId: string) =>
    requests.get(`manager/${restaurantId}/order/${orderId}`),
  updateOrderStatus: (
    restaurantId: string,
    orderId: string,
    orderStatusUpdate: OrderStatusUpdate
  ) =>
    requests.put(
      `manager/${restaurantId}/order/${orderId}/update-status`,
      orderStatusUpdate
    ),
  updateStatus: (
    orderId: string,
    restaurantId: string,
    newStatus: OrderStatus
  ) =>
    requests.put(`manager/${restaurantId}/order/${orderId}/status`, {
      status: newStatus,
    }),
};

const Kitchen = {
  list: (restaurantId: string) =>
    requests.get(`manager/${restaurantId}/kitchen`),
  details: (restaurantId: string, kitchenId: string) =>
    requests.get(`manager/${restaurantId}/kitchen/${kitchenId}`),
  create: (restaurantId: string, kitchen: KitchenFormModel) =>
    requests.post(`manager/${restaurantId}/kitchen`, kitchen),
  orders: (
    restaurantId: string,
    kitchenId: string,
    nextPageDate?: string,
    previousPageDate?: string,
    pageSize?: number,
    statuses?: OrderStatus[]
  ) => {
    return requests.get(`manager/${restaurantId}/kitchen/${kitchenId}/order`, {
      params: {
        nextPageDate,
        previousPageDate,
        pageSize,
        statuses: statuses?.join('%2C'),
      },
    });
  },
  updateOrderStatus: (
    restaurantId: string,
    orderId: string,
    kitchenId: string,
    orderStatusUpdate: OrderStatusUpdate
  ) =>
    requests.put(
      `manager/${restaurantId}/kitchen/${kitchenId}/order/${orderId}/update-status`,
      orderStatusUpdate
    ),
  update: (
    restaurantId: string,
    kitchenId: string,
    kitchen: KitchenFormModel
  ) => requests.put(`manager/${restaurantId}/kitchen/${kitchenId}`, kitchen),
  delete: (restaurantId: string, kitchenId: string) =>
    requests.delete(`manager/${restaurantId}/kitchen/${kitchenId}`),
  getMealItems: (restaurantId: string, kitchenId: string) =>
    requests.get(`manager/${restaurantId}/kitchen/${kitchenId}/meal-items`),
};

const Menu = {
  list: (restaurantId: string) => requests.get(`manager/${restaurantId}/menu`),
  details: (restaurantId: string, menuId: string) =>
    requests.get(`manager/${restaurantId}/menu/${menuId}`),
  formData: (restaurantId: string, menuId: string) =>
    requests.get(`manager/${restaurantId}/menu/${menuId}`),
  create: (restaurantId: string, menu: MenuFormModel) =>
    requests.post(`manager/${restaurantId}/menu`, menu),
  updateMenuMealItems: (
    restaurantId: string,
    menuId: string,
    orderedMealItemIds: string[]
  ) =>
    requests.post(
      `manager/${restaurantId}/menu/${menuId}/meal-items`,
      orderedMealItemIds
    ),
  update: (restaurantId: string, menuId: string, data: MenuFormModel) =>
    requests.put(`manager/${restaurantId}/menu/${menuId}`, data),
  updateOrder: (restaurantId: string, orderedMenuIds: string[]) =>
    requests.put(`manager/${restaurantId}/menu/reorder`, orderedMenuIds),
  delete: (restaurantId: string, menuId: string) =>
    requests.delete(`manager/${restaurantId}/menu/${menuId}`),
  getAvailabilities: (restaurantId: string, menuId: string) =>
    requests.get<AvailabilityScheduleDto[]>(
      `manager/${restaurantId}/menu/${menuId}/availability`
    ),
  updateAvailabilities: (
    restaurantId: string,
    menuId: string,
    data: UpdateAvailabilityScheduleDto
  ) =>
    requests.put(`manager/${restaurantId}/menu/${menuId}/availability`, data),
};

const MealItem = {
  list: (restaurantId: string) =>
    requests.get(`manager/${restaurantId}/meal-item`),
  details: (restaurantId: string, id: string) =>
    requests.get(`manager/${restaurantId}/meal-item/${id}`),
  create: (restaurantId: string, mealItem: MealItemFormModel) =>
    requests.postForm(`manager/${restaurantId}/meal-item`, mealItem),
  update: (
    restaurantId: string,
    mealItemId: string,
    mealItem: MealItemFormModel
  ) =>
    requests.putForm(
      `manager/${restaurantId}/meal-item/${mealItemId}`,
      mealItem
    ),
};

const MealItemCustomization = {
  list: (restaurantId: string) =>
    requests.get(`manager/${restaurantId}/meal-item-customization`),
  details: (restaurantId: string, customizationId: string) =>
    requests.get(
      `manager/${restaurantId}/meal-item-customization/${customizationId}`
    ),
  create: (restaurantId: string, data: MealItemCustomizationModel) =>
    requests.post(`manager/${restaurantId}/meal-item-customization`, data),
  update: (
    restaurantId: string,
    customizationId: string,
    data: MealItemCustomizationModel
  ) =>
    requests.put(
      `manager/${restaurantId}/meal-item-customization/${customizationId}`,
      data
    ),
  delete: (restaurantId: string, customizationId: string) =>
    requests.delete(
      `manager/${restaurantId}/meal-item-customization/${customizationId}`
    ),
  createOption: (
    restaurantId: string,
    customizationId: string,
    data: MealItemCustomizationOptionModel
  ) =>
    requests.post(
      `manager/${restaurantId}/meal-item-customization/${customizationId}/options`,
      data
    ),
  updateOption: (
    restaurantId: string,
    customizationId: string,
    optionId: string,
    data: MealItemCustomizationOptionModel
  ) =>
    requests.put(
      `manager/${restaurantId}/meal-item-customization/${customizationId}/options/${optionId}`,
      data
    ),
  deleteOption: (
    restaurantId: string,
    customizationId: string,
    optionId: string
  ) =>
    requests.delete(
      `manager/${restaurantId}/meal-item-customization/${customizationId}/options/${optionId}`
    ),
  updateRules: (
    restaurantId: string,
    customizationId: string,
    data: MealItemCustomizationRules
  ) =>
    requests.put(
      `manager/${restaurantId}/meal-item-customization/${customizationId}/rules`,
      data
    ),
  getLinked: (restaurantId: string, mealItemId: string) =>
    requests.get(
      `manager/${restaurantId}/meal-item/${mealItemId}/customizations`
    ),
  addToMealItem: (
    restaurantId: string,
    mealItemId: string,
    customizationId: string
  ) =>
    requests.post(
      `manager/${restaurantId}/meal-item/${mealItemId}/customizations/${customizationId}`,
      {}
    ),
  removeFromMealItem: (
    restaurantId: string,
    mealItemId: string,
    customizationId: string
  ) =>
    requests.delete(
      `manager/${restaurantId}/meal-item/${mealItemId}/customizations/${customizationId}`
    ),
};

const RestaurantTable = {
  list: (restaurantId: string) =>
    requests.get(`manager/${restaurantId}/restaurant-table`),
  details: (restaurantId: string, id: string) =>
    requests.get(`manager/${restaurantId}/restaurant-table/${id}`),
  create: (restaurantId: string, tableData: TableFormData) =>
    requests.post(`manager/${restaurantId}/restaurant-table`, {
      tableNumber: tableData.tableNumber,
      status: tableData.status,
      capacity: tableData.capacity,
      notes: tableData.notes,
    }),
  generateQrCode: (restaurantId: string, id: string) =>
    requests.get(`manager/${restaurantId}/restaurant-table/${id}/qrcode`),
  update: (restaurantId: string, tableId: string, data: TableFormData) =>
    requests.put(`manager/${restaurantId}/restaurant-table/${tableId}`, {
      tableNumber: data.tableNumber,
      status: data.status,
      capacity: data.capacity,
      notes: data.notes,
    }),
  delete: (restaurantId: string, tableId: string) =>
    requests.delete(`manager/${restaurantId}/restaurant-table/${tableId}`),
  updateStatus: (restaurantId: string, tableId: string, status: TableStatus) =>
    requests.put(`manager/${restaurantId}/restaurant-table/${tableId}/status`, {
      status,
    }),
};

const RestaurantUser = {
  list: (restaurantId: string) =>
    requests.get(`manager/${restaurantId}/restaurant-user`),
  userRoles: (restaurantId: string, id: string) =>
    requests.get<RestaurantUserRoleDto>(
      `manager/${restaurantId}/restaurant-user/${id}/roles`
    ),
  create: (restaurantId: string, userData: FieldValues) =>
    requests.post(`manager/${restaurantId}/restaurant-user`, userData),
  delete: (restaurantId: string, email: string) =>
    requests.delete(`manager/${restaurantId}/restaurant-user?email=${email}`),
  update: (restaurantId: string, data: UpdateRestaurantUserFormData) =>
    requests.put(`manager/${restaurantId}/restaurant-user`, data),
  resendVerificationEmail: (restaurantId: string, email: string) =>
    requests.post(
      `manager/${restaurantId}/restaurant-user/resend-verification`,
      { email }
    ),
};

const CustomerRestaurant = {
  details: (restaurantId: string) =>
    requests.get(`customer-restaurant/${restaurantId}`),
  list: () => requests.get('customer-restaurant'),
  tables: (restaurantId: string) =>
    requests.get(`customer-restaurant/${restaurantId}/tables`),
};

const Cart = {
  create: (restaurantId: string, cart: CartModel) =>
    requests.post(`manager/${restaurantId}/order`, cart),
};

const Account = {
  login: (values: FieldValues) =>
    requests.post('account/login', values).then((response) => {
      authService.saveUser(response);
      const userWithRoles = authService.getUserFromLocalStorage();
      console.log('User with roles:', userWithRoles);
      if (userWithRoles) {
        store.dispatch(setUser(userWithRoles));
      }
      return response;
    }),
  register: (values: FieldValues) => requests.post('account/register', values),
  resetPassword: (body: ResetPasswordRequestModel) =>
    requests.post('account/reset-password', body),
  verifyEmail: (token: string, email: string) =>
    requests.get(`account/verify-email?email=${email}&token=${token}`),
  forgotPassword: (body: ForgotPasswordRequestModel) =>
    requests.post('account/forgot-password', body),
  changePassword: (body: ChangePasswordRequestModel) =>
    requests.post('account/change-password', body),
  refreshToken: async (email: string, refreshToken: string) => {
    try {
      const response = await requests.post('account/refresh-token', {
        email,
        refreshToken,
      });

      // Save the user data to localStorage for persistence
      authService.saveUser(response);

      return response;
    } catch (error) {
      if (
        (error as AxiosError).response &&
        (error as AxiosError).response?.status === 401
      ) {
        store.dispatch(logout());
        authService.resetUser();
      }
      throw error;
    }
  },
  logout: (body: { email: string; refreshToken: string }) =>
    requests.post('account/logout', body),
  updateProfile: (data: UserProfileFormData) =>
    requests.putForm('account/profile', data),
};

const Antiforgery = {
  token: async () => {
    const response = await requests.get('manager/antiforgery');
    return response.data.token;
  },
};

const Logs = {
  download: (from?: string, to?: string) => {
    const params: { from?: string; to?: string; _?: number } = { from, to };
    // Add a cache-busting parameter
    params._ = Date.now();
    return requests.getBlob('admin/logs/download', { params });
  },
};

const KitchenStaffRestaurant = {
  list: () => requests.get('kitchen-staff/restaurant'),
  details: (restaurantId: string) =>
    requests.get(`kitchen-staff/restaurant/${restaurantId}`),
  kitchens: (restaurantId: string) =>
    requests.get(`manager/${restaurantId}/kitchen`),
};

const RestaurantWaiter = {
  restaurants: (pageNumber: number, pageSize: number) =>
    requests.get('waiter/restaurant', { params: { pageNumber, pageSize } }),
  details: (restaurantId: string) =>
    requests.get(`waiter/restaurant/${restaurantId}`),
  tables: (restaurantId: string) =>
    requests.get(`waiter/restaurant/${restaurantId}/tables`),
  orders: (restaurantId: string, waiterId: string, nextPageId?: string) =>
    requests.get(`waiter/restaurant/${restaurantId}/order/waiter/${waiterId}`, {
      params: { nextPageId },
    }),
};

const KitchenStaffMealItem = {
  list: async (kitchenId: string, params: URLSearchParams) =>
    requests.get(`/kitchen-staff/kitchens/${kitchenId}/meal-items`, {
      params:
        params instanceof URLSearchParams ? Object.fromEntries(params) : params,
    }),

  details: async (kitchenId: string, mealItemId: string) =>
    requests.get(
      `/kitchen-staff/kitchens/${kitchenId}/meal-items/${mealItemId}`
    ),

  updateAvailability: async (
    kitchenId: string,
    mealItemId: string,
    isAvailable: boolean
  ) =>
    requests.patch(
      `/kitchen-staff/kitchens/${kitchenId}/meal-items/${mealItemId}/availability`,
      { isAvailable }
    ),
};

// Add Payment related endpoints
const Payments = {
  create: (payment: CreatePaymentRequest) => requests.post('payments', payment),
  getByOrder: (orderId: string) => requests.get(`payments/order/${orderId}`),
  getByRestaurant: (restaurantId: string, nextPageId?: string) =>
    requests.get(`payments/restaurant/${restaurantId}`, {
      params: { nextPageId },
    }),
};

// Add Refund related endpoints
const Refunds = {
  create: (refund: CreateRefundRequest) => requests.post('refunds', refund),
  getByOrder: (orderId: string) => requests.get(`refunds/order/${orderId}`),
  getByRestaurant: (restaurantId: string, nextPageId?: string) =>
    requests.get(`refunds/restaurant/${restaurantId}`, {
      params: { nextPageId },
    }),
};

const TaxRate = {
  list: (restaurantId: string) =>
    requests.get(`restaurants/${restaurantId}/tax-rates`),
  listLocalized: (restaurantId: string, languageCode?: string) => {
    const url = `restaurants/${restaurantId}/tax-rates`;
    const params: { languageCode?: string } = {};
    if (languageCode) {
      params.languageCode = languageCode;
    }
    return requests.get(url, { params });
  },
  details: (restaurantId: string, taxRateId: string) =>
    requests.get(`restaurants/${restaurantId}/tax-rates/${taxRateId}`),
  create: (restaurantId: string, data: TaxRateFormModel) =>
    requests.post(`restaurants/${restaurantId}/tax-rates`, data),
  update: (restaurantId: string, taxRateId: string, data: TaxRateFormModel) =>
    requests.put(`restaurants/${restaurantId}/tax-rates/${taxRateId}`, data),
  delete: (restaurantId: string, taxRateId: string) =>
    requests.delete(`restaurants/${restaurantId}/tax-rates/${taxRateId}`),
  updateMealItemTaxRate: (
    restaurantId: string,
    mealItemId: string,
    taxRateId: string | null
  ) =>
    requests.put(
      `restaurants/${restaurantId}/meal-items/${mealItemId}/tax-rate`,
      { taxRateId }
    ),
};

const api = {
  Restaurant,
  Kitchen,
  Menu,
  MealItem,
  MealItemCustomization,
  RestaurantTable,
  CustomerRestaurant,
  Cart,
  Account,
  Antiforgery,
  Order,
  RestaurantUser,
  KitchenStaffRestaurant,
  RestaurantWaiter,
  KitchenStaffMealItem,
  Payments,
  Refunds,
  Logs,
  TaxRate,
};

export default api;
